import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { URLS } from '../constants/urls';
import { parseApiResponse } from '../services/apiResponse';

const AuthContext = createContext();

const STORAGE_TOKEN = 'token';
const STORAGE_USER = 'usuario';
const STORAGE_TIPO = 'tipoUsuario';
const STORAGE_JWT_META = 'jwtMeta'; // { issuedAt, expiresAt, issuer }

function decodeField(token, field) {
  try {
    const dec = jwtDecode(token);
    return typeof dec?.[field] === 'number' ? dec[field] : null;
  } catch (e) {
    console.warn('[Auth] decodeField error:', e);
    return null;
  }
}

function decodeAll(token) {
  try {
    return jwtDecode(token);
  } catch (e) {
    return null;
  }
}

function trunc(str, head = 12, tail = 8) {
  if (!str || typeof str !== 'string') return str;
  if (str.length <= head + tail) return str;
  return `${str.slice(0, head)}...${str.slice(-tail)}`;
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [usuario, setUsuario] = useState(null);
  const [tipoUsuario, setTipoUsuario] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // meta de tempo
  const [issuedAt, setIssuedAt] = useState(null);   // epoch s
  const [expiresAt, setExpiresAt] = useState(null); // epoch s
  const [secondsLeft, setSecondsLeft] = useState(null);

  const checkerRef = useRef(null);
  const renewingRef = useRef(false);

  const persist = (nextToken, nextUser, nextTipo, meta) => {
    if (nextToken) localStorage.setItem(STORAGE_TOKEN, nextToken);
    if (nextUser) localStorage.setItem(STORAGE_USER, JSON.stringify(nextUser));
    if (nextTipo) localStorage.setItem(STORAGE_TIPO, nextTipo);
    if (meta) localStorage.setItem(STORAGE_JWT_META, JSON.stringify(meta));
  };

  const clearPersisted = () => {
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_USER);
    localStorage.removeItem(STORAGE_TIPO);
    localStorage.removeItem(STORAGE_JWT_META);
  };

  const logout = () => {
    console.info('[Auth] logout()');
    setToken(null);
    setUsuario(null);
    setTipoUsuario(null);
    setIssuedAt(null);
    setExpiresAt(null);
    setSecondsLeft(null);
    clearPersisted();
  };

  // Bootstrap
  useEffect(() => {
    const storedToken = localStorage.getItem(STORAGE_TOKEN);
    const storedUser = localStorage.getItem(STORAGE_USER);
    const storedTipo = localStorage.getItem(STORAGE_TIPO);
    const storedMeta = localStorage.getItem(STORAGE_JWT_META);

    if (storedToken && storedUser && storedTipo) {
      try {
        const meta = storedMeta ? JSON.parse(storedMeta) : null;
        const exp = meta?.expiresAt ?? decodeField(storedToken, 'exp');
        const iat = meta?.issuedAt ?? decodeField(storedToken, 'iat');
        const now = Math.floor(Date.now() / 1000);
        const ttl = exp && iat ? (exp - iat) : null;

        if (exp && exp < now) {
          console.warn('[Auth] token expirado no bootstrap, fazendo logout');
          logout();
        } else {
          setToken(storedToken);
          setUsuario(JSON.parse(storedUser));
          setTipoUsuario(storedTipo);
          setIssuedAt(iat ?? null);
          setExpiresAt(exp ?? null);
        }
      } catch (e) {
        console.warn('[Auth] erro ao restaurar do storage:', e);
        logout();
      }
    } else {
      console.log('[Auth] storage incompleto; ignorando restauração.');
    }

    setAuthChecked(true);
  }, []);

  // Relógio (1s) — mantém secondsLeft atualizado
  useEffect(() => {
    if (checkerRef.current) {
      clearInterval(checkerRef.current);
      checkerRef.current = null;
    }
    if (!token || !expiresAt) {
      setSecondsLeft(null);
      return;
    }
    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      const left = Math.max(0, expiresAt - now);
      setSecondsLeft(left);
      if (left === 0) {
        console.warn('[Auth] token chegou a 0s — logout');
        logout();
      }
    };
    tick();
    checkerRef.current = setInterval(tick, 1000);
    return () => {
      if (checkerRef.current) clearInterval(checkerRef.current);
      checkerRef.current = null;
    };
  }, [token, expiresAt]);

  // Renovar token
  const renewToken = async () => {
    if (!token || renewingRef.current) {
      console.warn('[Auth] renewToken bloqueado: sem token ou já renovando');
      return { success: false, message: 'Sem token ou já renovando' };
    }
    renewingRef.current = true;

    try {
      const res = await fetch(URLS.RENEW_JWT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: '{}',
      });
      const data = await res.json();

      if (!data?.success || !data?.token) {
        console.warn('[Auth] renew falhou:', data?.message);
        return { success: false, message: data?.message || 'Falha ao renovar' };
      }

      const nextToken = data.token;
      const meta = data.jwt ?? {};
      const expServer = typeof meta.expiresAt === 'number' ? meta.expiresAt : null;
      const iatServer = typeof meta.issuedAt === 'number' ? meta.issuedAt : null;
      const dec = decodeAll(nextToken);

      const expDec = dec?.exp ?? null;
      const iatDec = dec?.iat ?? null;

      const nextExp = expServer ?? expDec ?? null;
      const nextIat = iatServer ?? iatDec ?? null;

      setToken(nextToken);
      setIssuedAt(nextIat);
      setExpiresAt(nextExp);

      // mantém usuário/tipo
      persist(nextToken, usuario, tipoUsuario, {
        ...(data.jwt ?? {}),
        issuedAt: nextIat,
        expiresAt: nextExp,
      });

      return { success: true, token: nextToken, jwt: { issuedAt: nextIat, expiresAt: nextExp, ...(data.jwt ?? {}) } };
    } catch (e) {
      console.error('[Auth] renewToken erro de rede:', e);
      return { success: false, message: 'Erro de rede ao renovar' };
    } finally {
      renewingRef.current = false;
    }
  };

  // Login Admin
  const login = async (credentials, legacySenha, legacyPais) => {
    const { telefone, senha, pais } = typeof credentials === 'object'
      ? credentials
      : { telefone: credentials, senha: legacySenha, pais: legacyPais };
    try {
      const response = await fetch(URLS.LOGIN_ADMIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telefone, senha, pais }),
      });
      const data = await parseApiResponse(response);

      if (data.success) {
        const nextToken = data.token;
        const nextUser = data.usuario;
        const nextTipo = 'admin';

        const expServer = typeof data?.jwt?.expiresAt === 'number' ? data.jwt.expiresAt : null;
        const iatServer = typeof data?.jwt?.issuedAt === 'number' ? data.jwt.issuedAt : null;

        const dec = decodeAll(nextToken);

        const expDec = dec?.exp ?? null;
        const iatDec = dec?.iat ?? null;

        const nextExp = expServer ?? expDec ?? null;
        const nextIat = iatServer ?? iatDec ?? null;

        setToken(nextToken);
        setUsuario(nextUser);
        setTipoUsuario(nextTipo);
        setIssuedAt(nextIat);
        setExpiresAt(nextExp);

        persist(nextToken, nextUser, nextTipo, {
          ...(data.jwt ?? {}),
          issuedAt: nextIat,
          expiresAt: nextExp,
        });

        return { ...data, success: true, token: nextToken };
      } else {
        console.warn('[Auth] login falhou:', data.code || data.status);
        return { ...data, success: false, message: data.message };
      }
    } catch (e) {
      console.error('[Auth] login erro de rede/servidor:', e);
      return { success: false, message: 'Erro de rede ou servidor' };
    }
  };

  // Login Participante
  const loginParticipante = async (credentials, legacySenha, legacyPais) => {
    const { celular, senha, pais } = typeof credentials === 'object'
      ? credentials
      : { celular: credentials, senha: legacySenha, pais: legacyPais };
    try {
      const response = await fetch(URLS.LOGIN_PARTICIPANTE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ celular, senha, pais }),
      });
      const data = await parseApiResponse(response);

      if (data.success) {
        const nextToken = data.token;
        const nextUser = data.participante;
        const nextTipo = 'participante';

        const expServer = typeof data?.jwt?.expiresAt === 'number' ? data.jwt.expiresAt : null;
        const iatServer = typeof data?.jwt?.issuedAt === 'number' ? data.jwt.issuedAt : null;

        const dec = decodeAll(nextToken);

        const expDec = dec?.exp ?? null;
        const iatDec = dec?.iat ?? null;

        const nextExp = expServer ?? expDec ?? null;
        const nextIat = iatServer ?? iatDec ?? null;

        setToken(nextToken);
        setUsuario(nextUser);
        setTipoUsuario(nextTipo);
        setIssuedAt(nextIat);
        setExpiresAt(nextExp);

        persist(nextToken, nextUser, nextTipo, {
          ...(data.jwt ?? {}),
          issuedAt: nextIat,
          expiresAt: nextExp,
        });

        return { ...data, success: true, token: nextToken };
      } else {
        console.warn('[Auth] login participante falhou:', data.code || data.status);
        return { ...data, success: false, message: data.message };
      }
    } catch (e) {
      console.error('[Auth] login participante erro de rede/servidor:', e);
      return { success: false, message: 'Erro de rede ou servidor' };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        usuario,
        tipoUsuario,
        isAuthenticated: !!token,
        authChecked,

        // meta de tempo
        issuedAt,
        expiresAt,
        secondsLeft,

        // ações
        renewToken,
        login,
        loginParticipante,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
